import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import Box from 'ui-box';
import config from '../../../config';
import {
  useToast,
  majorScale,
  Button,
  Checkbox,
  FormField,
  InputGroup,
} from '../../../npaper';
import { useStores } from '../../../stores';
import validateEmail from '../../../utils/email-validator';
import { AuthTitle } from '../AuthLayout';

interface CreateAccountValues {
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
  marketing: boolean;
}

const CreateUserStep = ({ onCreate }: { onCreate: () => void }) => {
  const intl = useIntl();
  const toaster = useToast();
  const { authStore } = useStores();
  const { register, handleSubmit, errors, setError, watch, control } =
    useForm<CreateAccountValues>();

  const onSubmit = async (values: CreateAccountValues) => {
    try {
      await authStore.signUp({
        email: values.email,
        password: values.password,
        marketing: values.marketing,
      });
      await authStore.setup();
      onCreate();
    } catch (error) {
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data['email']) {
          setError('email', {
            message: intl.formatMessage({
              defaultMessage: 'A user already exists with that email',
            }),
          });
          return;
        }
        if (data['password']) {
          setError('password', {
            message: data['password'].join(' '),
          });
          return;
        }
      }
      toaster.showDefaultError(error);
    }
  };

  return (
    <Box>
      <AuthTitle
        title="Opret din Sails konto"
        sub="Kom hurtigt igang med Sails"
      />

      <FormField label="Email" marginBottom={majorScale(2)} large>
        <InputGroup
          placeholder="Email"
          name="email"
          ref={register({
            required: intl.formatMessage({
              defaultMessage: 'Please enter your email to sign up',
            }),
            validate: (v: string) =>
              validateEmail(v) ||
              intl.formatMessage({
                defaultMessage: 'Please provide a valid email address',
              }),
          })}
          isInvalid={!!errors.email}
          validationMessage={errors.email?.message}
        />
      </FormField>

      <FormField label="Kodeord" marginBottom={majorScale(2)} large>
        <InputGroup
          placeholder="Kodeord"
          type="password"
          name="password"
          ref={register({
            required: intl.formatMessage({
              defaultMessage: 'Please provide a password',
            }),
          })}
          isInvalid={!!errors.password}
          validationMessage={errors.password?.message}
        />
        <InputGroup
          placeholder="Gentag kodeord"
          type="password"
          name="confirmPassword"
          ref={register({
            validate: (val: string) =>
              val === watch('password') ||
              intl.formatMessage({
                defaultMessage: 'Passwords do not match',
              }),
          })}
          isInvalid={!!errors.confirmPassword}
          validationMessage={
            errors.confirmPassword && errors.confirmPassword.message
          }
        />
      </FormField>

      <Controller
        control={control}
        name="acceptTerms"
        rules={{ required: true }}
        render={({ onChange, value }) => (
          <Checkbox
            small
            onChange={(f) => onChange(f)}
            checked={value}
            label={
              <span className={!!errors.acceptTerms ? 'danger' : ''}>
                Ved at oprette en bruger accepterer du vores{' '}
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  className="link small"
                  href={config.privacyUrl}
                  target="_blank"
                >
                  Privatlivspolitik
                </a>{' '}
                og {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  className="link small"
                  href={config.termsUrl}
                  target="_blank"
                >
                  Forretningsbetingelser
                </a>
                .
              </span>
            }
            marginBottom={majorScale(2)}
          />
        )}
      />
      <Controller
        control={control}
        name="marketing"
        render={({ onChange, value }) => (
          <Checkbox
            small
            label="Vil du modtage nye produktinformationer og markedsføringmateriale fra os?"
            onChange={(f) => onChange(f)}
            checked={value}
          />
        )}
      />

      <Button
        width="100%"
        primary
        marginBottom={majorScale(2)}
        marginTop={majorScale(2)}
        onClick={handleSubmit(onSubmit)}
      >
        Opret konto
      </Button>

      <Box
        is={Link}
        to="/auth"
        className="a"
        marginBottom={majorScale(2)}
        display="block"
        textAlign="center"
      >
        Har du allerede en konto?
      </Box>
    </Box>
  );
};

export default CreateUserStep;
